import { useState, useRef, useEffect } from 'react';
import { NoteKey } from '../hooks';

interface PortfolioItem {
  title: string;
  category: string;
  description: string;
  image: string;
}

interface PortfolioProps {
  title: string;
  subtitle: string;
  items: PortfolioItem[];
  isVisible: boolean;
  onPlayNote: (note: NoteKey) => void;
  viewLabel: string;
  closeLabel: string;
}

export default function Portfolio({
  title,
  subtitle,
  items,
  isVisible,
  onPlayNote,
  viewLabel,
  closeLabel,
}: PortfolioProps) {
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Auto-rotation effect
  useEffect(() => {
    if (!autoRotate || isDragging) return;

    const interval = setInterval(() => {
      setRotation((prev) => (prev + 0.5) % 360);
    }, 50);

    return () => clearInterval(interval);
  }, [autoRotate, isDragging]);

  // Handle mouse/touch drag for rotation
  const handleDragStart = (clientX: number) => {
    setIsDragging(true);
    setAutoRotate(false);
    setStartX(clientX);
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return;
    const delta = clientX - startX;
    setRotation((prev) => prev + delta * 0.5);
    setStartX(clientX);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // Resume auto-rotation after 3 seconds of inactivity
    setTimeout(() => setAutoRotate(true), 3000);
  };

  const openDialog = (item: PortfolioItem) => {
    setSelectedItem(item);
    setAutoRotate(false);
    onPlayNote('E5');
  };

  const closeDialog = () => {
    setSelectedItem(null);
    setTimeout(() => setAutoRotate(true), 1000);
    onPlayNote('C5');
  };

  // Calculate positions for 3D carousel
  const itemCount = items.length;
  const angleStep = 360 / itemCount;
  const radius = 300;

  return (
    <section className="py-32 px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy-mid)] to-[var(--navy-deep)]" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{title}</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* 3D Rotating Carousel */}
        <div
          ref={carouselRef}
          className={`relative h-[500px] transition-opacity duration-1000 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ perspective: '1000px' }}
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseMove={(e) => handleDragMove(e.clientX)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
          onTouchEnd={handleDragEnd}
        >
          <div
            className="absolute inset-0 flex items-center justify-center transition-transform duration-100"
            style={{
              transformStyle: 'preserve-3d',
              transform: `rotateY(${rotation}deg)`,
            }}
          >
            {items.map((item, i) => {
              const angle = angleStep * i;
              const x = Math.sin((angle * Math.PI) / 180) * radius;
              const z = Math.cos((angle * Math.PI) / 180) * radius;

              return (
                <div
                  key={i}
                  className="absolute w-64 h-80 cursor-pointer group"
                  style={{
                    transform: `translateX(${x}px) translateZ(${z}px) rotateY(${-rotation}deg)`,
                    transformStyle: 'preserve-3d',
                  }}
                  onClick={() => openDialog(item)}
                  onMouseEnter={() => onPlayNote(['C5', 'D5', 'E5', 'G5', 'A5'][i % 5] as NoteKey)}
                >
                  {/* Card */}
                  <div className="w-full h-full rounded-xl bg-white/5 border border-white/20 backdrop-blur-sm overflow-hidden group-hover:border-[var(--cyan-electric)]/50 transition-all duration-300 group-hover:scale-105">
                    {/* Image or Blueprint fallback */}
                    <div className="h-48 bg-gradient-to-br from-[var(--navy-light,#1a2744)] to-[var(--navy-mid)] relative overflow-hidden">
                      {item.image ? (
                        <>
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              // Hide broken image and show blueprint fallback
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          {/* Blueprint fallback (hidden by default) */}
                          <div className="hidden absolute inset-0">
                            <div
                              className="absolute inset-0 opacity-30"
                              style={{
                                backgroundImage: `
                                  linear-gradient(rgba(0, 212, 255, 0.3) 1px, transparent 1px),
                                  linear-gradient(90deg, rgba(0, 212, 255, 0.3) 1px, transparent 1px)
                                `,
                                backgroundSize: '20px 20px',
                              }}
                            />
                            <svg
                              className="absolute inset-0 w-full h-full text-[var(--cyan-electric)] opacity-60"
                              viewBox="0 0 200 150"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="0.5"
                            >
                              <circle cx="100" cy="75" r="40" />
                              <circle cx="100" cy="75" r="30" strokeDasharray="4 2" />
                              <circle cx="100" cy="75" r="20" />
                              <line x1="60" y1="75" x2="140" y2="75" />
                              <line x1="100" y1="35" x2="100" y2="115" />
                            </svg>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Grid overlay for blueprint effect */}
                          <div
                            className="absolute inset-0 opacity-30"
                            style={{
                              backgroundImage: `
                                linear-gradient(rgba(0, 212, 255, 0.3) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(0, 212, 255, 0.3) 1px, transparent 1px)
                              `,
                              backgroundSize: '20px 20px',
                            }}
                          />

                          {/* Abstract industrial design shape */}
                          <svg
                            className="absolute inset-0 w-full h-full text-[var(--cyan-electric)] opacity-60 group-hover:opacity-100 transition-opacity"
                            viewBox="0 0 200 150"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="0.5"
                          >
                            {/* Different design patterns for each item */}
                            {i % 4 === 0 && (
                              <>
                                <circle cx="100" cy="75" r="40" />
                                <circle cx="100" cy="75" r="30" strokeDasharray="4 2" />
                                <circle cx="100" cy="75" r="20" />
                                <line x1="60" y1="75" x2="140" y2="75" />
                                <line x1="100" y1="35" x2="100" y2="115" />
                              </>
                            )}
                            {i % 4 === 1 && (
                              <>
                                <rect x="50" y="30" width="100" height="90" rx="5" />
                                <rect x="65" y="45" width="70" height="60" rx="3" strokeDasharray="4 2" />
                                <circle cx="100" cy="75" r="15" />
                                <line x1="50" y1="50" x2="65" y2="50" />
                                <line x1="135" y1="50" x2="150" y2="50" />
                              </>
                            )}
                            {i % 4 === 2 && (
                              <>
                                <polygon points="100,25 150,75 130,125 70,125 50,75" />
                                <polygon points="100,40 135,75 120,110 80,110 65,75" strokeDasharray="4 2" />
                                <circle cx="100" cy="75" r="10" fill="currentColor" opacity="0.3" />
                              </>
                            )}
                            {i % 4 === 3 && (
                              <>
                                <ellipse cx="100" cy="75" rx="60" ry="40" />
                                <ellipse cx="100" cy="75" rx="40" ry="25" strokeDasharray="4 2" />
                                <line x1="40" y1="75" x2="160" y2="75" />
                                <line x1="100" y1="35" x2="100" y2="115" />
                                <circle cx="100" cy="75" r="8" fill="currentColor" opacity="0.3" />
                              </>
                            )}
                          </svg>

                          {/* Measurement annotations */}
                          <div className="absolute bottom-2 left-2 text-xs text-[var(--cyan-electric)]/70 font-mono">
                            DWG-{String(i + 1).padStart(3, '0')}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <span className="text-xs text-[var(--amber-warm)] uppercase tracking-wider">
                        {item.category}
                      </span>
                      <h3 className="text-lg font-semibold mt-1 group-hover:text-[var(--cyan-electric)] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-400 mt-2 line-clamp-2">{item.description}</p>
                    </div>

                    {/* View button */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-sm text-[var(--cyan-electric)]">{viewLabel} →</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Center focus indicator */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[var(--cyan-electric)]/30 blur-sm pointer-events-none" />
        </div>

        {/* Drag hint */}
        <p className="text-center text-gray-500 text-sm mt-8">
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
            Drag to rotate
          </span>
        </p>
      </div>

      {/* Dialog Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={closeDialog}
        >
          <div
            className="relative max-w-4xl w-full bg-[var(--navy-mid)] rounded-2xl border border-white/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeDialog}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="grid md:grid-cols-2 gap-0">
              {/* Design preview */}
              <div className="h-64 md:h-auto min-h-[300px] bg-gradient-to-br from-[var(--navy-light,#1a2744)] to-[var(--navy-deep)] relative flex items-center justify-center">
                {selectedItem.image ? (
                  <>
                    <img
                      src={selectedItem.image}
                      alt={selectedItem.title}
                      className="w-full h-full object-contain p-4"
                    />
                    {/* Annotations */}
                    <div className="absolute bottom-4 left-4 text-xs text-[var(--cyan-electric)]/70 font-mono">
                      {selectedItem.category}
                    </div>
                    <div className="absolute top-4 left-4 text-xs text-[var(--cyan-electric)]/70 font-mono">
                      FLUXCONN DESIGN
                    </div>
                  </>
                ) : (
                  <>
                    {/* Grid overlay */}
                    <div
                      className="absolute inset-0 opacity-30"
                      style={{
                        backgroundImage: `
                          linear-gradient(rgba(0, 212, 255, 0.3) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(0, 212, 255, 0.3) 1px, transparent 1px)
                        `,
                        backgroundSize: '30px 30px',
                      }}
                    />

                    {/* Larger blueprint design */}
                    <svg
                      className="absolute inset-0 w-full h-full text-[var(--cyan-electric)] p-8"
                      viewBox="0 0 200 200"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                    >
                      <circle cx="100" cy="100" r="70" />
                      <circle cx="100" cy="100" r="55" strokeDasharray="4 2" />
                      <circle cx="100" cy="100" r="40" />
                      <circle cx="100" cy="100" r="25" strokeDasharray="4 2" />
                      <circle cx="100" cy="100" r="10" fill="currentColor" opacity="0.3" />
                      <line x1="20" y1="100" x2="180" y2="100" />
                      <line x1="100" y1="20" x2="100" y2="180" />
                      <line x1="40" y1="40" x2="160" y2="160" strokeDasharray="8 4" opacity="0.5" />
                      <line x1="160" y1="40" x2="40" y2="160" strokeDasharray="8 4" opacity="0.5" />
                    </svg>

                    {/* Annotations */}
                    <div className="absolute bottom-4 left-4 text-xs text-[var(--cyan-electric)]/70 font-mono">
                      SCALE: 1:1 | REV: A
                    </div>
                    <div className="absolute top-4 left-4 text-xs text-[var(--cyan-electric)]/70 font-mono">
                      FLUXCONN DESIGN
                    </div>
                  </>
                )}
              </div>

              {/* Details */}
              <div className="p-8">
                <span className="text-sm text-[var(--amber-warm)] uppercase tracking-wider">
                  {selectedItem.category}
                </span>
                <h3 className="text-3xl font-bold mt-2 mb-4">{selectedItem.title}</h3>
                <p className="text-gray-300 leading-relaxed mb-6">{selectedItem.description}</p>

                {/* Specs */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-[var(--cyan-electric)]" />
                    <span className="text-gray-400">Industrial-grade components</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-[var(--cyan-electric)]" />
                    <span className="text-gray-400">Custom firmware support</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-[var(--cyan-electric)]" />
                    <span className="text-gray-400">Full documentation included</span>
                  </div>
                </div>

                <button
                  onClick={closeDialog}
                  className="btn-rgb-hover px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--cyan-electric)] to-cyan-400 text-[var(--navy-deep)] font-medium transition-all"
                >
                  {closeLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
